// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsStyle } from "@guestsupply/App/Components/Product/ProductActions";
import loadFeaturedProducts from "@guestsupply/App/Handlers/XNG/FeaturedProducts";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import LazyImage from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

interface FeaturedProductsCardProps {
    product: ProductModel;
}

const mapStateToProps = (state: CustomAppState) => ({
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadFeaturedProducts,
    updateProductInfo,
    setNavDrawerIsOpen,
};

type Props = FeaturedProductsCardProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface FeaturedProductsCardStyle {
    wrapper?: InjectableCss;
    imageLink?: LinkProps;
    titleLink?: LinkProps;
    erpNumberText?: SmallHeadingAndTextStyles;
    image?: LazyImageProps;
    contentWrapper?: InjectableCss;
    productActionsStyle?: ProductActionsStyle;
}

export const featuredProductsCardStyles: FeaturedProductsCardStyle = {};

const styles = featuredProductsCardStyles;

const FeaturedProductsCard: FC<Props> = ({ product, setNavDrawerIsOpen, isTabletScreen }) => {
    const productInfo = createFromProduct(product);
    const [qty, setQty] = useState(productInfo.qtyOrdered);
    const [unitOfMeasure, setUnitOfMeasure] = useState(productInfo.unitOfMeasure);

    const updateQuantity = (qtyOrdered: number) => {
        setQty(qtyOrdered);
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        setUnitOfMeasure(unitOfMeasure);
    };

    const productContext = {
        product,
        productInfo: {
            ...productInfo,
            qtyOrdered: qty,
            unitOfMeasure,
        },
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    const handleLinkClick = () => {
        if (isTabletScreen) {
            // XNG-CHANGES : Moving ootb behaviour setNavDrawerIsOpen to false
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
            return;
        }
        hideAll();
    };
    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <Link
                    {...styles.imageLink}
                    href={productInfo.productDetailPath}
                    onClick={handleLinkClick}
                    aria-label={product.imageAltText}
                >
                    <LazyImage
                        {...styles.image}
                        width="98"
                        height="104"
                        src={product.smallImagePath || product.mediumImagePath || product.largeImagePath}
                        altText={product.imageAltText}
                    />
                </Link>
                <StyledWrapper {...styles.contentWrapper}>
                    <Link
                        {...styles.titleLink}
                        href={productInfo.productDetailPath}
                        onClick={handleLinkClick}
                        aria-label={product.productTitle}
                    >
                        {product.productTitle}
                    </Link>
                    {product.productNumber && (
                        <SmallHeadingAndText
                            heading={translate("SKU")}
                            text={product.productNumber}
                            extendedStyles={styles.erpNumberText}
                        />
                    )}
                    <ProductActions iconOnly={true} discardPrice={true} extendedStyles={styles.productActionsStyle} />
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FeaturedProductsCard);
